import { createContext, useContext, useEffect, useState } from "react";
import { useOktaAuth } from "@okta/okta-react";
import { useLocation } from "react-router-dom";
import config from "../config";
import {
  EMPTY_SCHOOL_LINK_SNAPSHOT,
  isMilestone1Route,
  parseCanvasQuicklinkBody,
  parseSchoolLinkResponse,
  type SchoolLinkApiResponse,
  type SchoolLinkSnapshot,
} from "@/utils/schoolLinkKind";
import { useOverviewContext } from "./OverviewContext";
import { isTeacherPreview } from "@/utils/toolSettingsUtils";
import { roleFromOktaClaims } from "@/utils/teacherUserApiFallback";

interface LinksContextType {
  myAccountLink: string | null;
  k12ZoneLink: string | null;
  schoolLink: SchoolLinkSnapshot;
  /** `true` after the initial quicklinks fetch attempt finishes (success or failure). */
  quicklinksLoaded: boolean;
  acknowledgement: AcknowledgementLink | null;

  fetchAcknowledgement: (schoolId: string, schoolShortName: string) => Promise<void>;
}

interface AcknowledgementLink {
  acknowledgementDocumentLink: string | null;
}

const LinksContext = createContext<LinksContextType | undefined>(undefined);

export type { SchoolLinkSnapshot };

export const LinksProvider = ({ children }: { children: React.ReactNode }) => {
  const { authState } = useOktaAuth();
  const { pathname } = useLocation();
  const { userRoleVal, loading: overviewLoading } = useOverviewContext();
  const oktaAccessToken = authState?.accessToken?.accessToken;
  const useCanvasQuicklinkApi = isMilestone1Route(pathname);

  const [myAccountLink, setMyAccountLink] = useState<string | null>(null);
  const [k12ZoneLink, setK12ZoneLink] = useState<string | null>(null);
  const [schoolLink, setSchoolLink] = useState<SchoolLinkSnapshot>(EMPTY_SCHOOL_LINK_SNAPSHOT);
  const [quicklinksLoaded, setQuicklinksLoaded] = useState(false);
  const [acknowledgement, setAcknowledgement] =
    useState<AcknowledgementLink | null>(null);

  const QUICKLINKS_API_URL = config.api.endpoints.QUICKLINKS;
  const SCHOOL_LINK_API_URL = config.api.endpoints.QUICKLINKS_SCHOOL_LINK;
  const CANVAS_QUICKLINK_API_URL = config.api.endpoints.QUICKLINKS_CANVAS;
  const ACKNOWLEDGEMENT_API_URL = config.api.endpoints.ACKNOWLEDGEMENT;

  /**
   * Fetch myaccount, K12Zone quicklinks from API
   */
  const fetchQuickLink = async (type: "myaccount" | "k12zone") => {
    if (!oktaAccessToken) {
      console.warn(" No Okta token available. Skipping API call.");
      return null;
    }
    try {
      const response = await fetch(`${QUICKLINKS_API_URL}/${type}`,
        {
          method: "GET",
          headers: {
            "Authorization": `Bearer ${oktaAccessToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(` API Error (${response.status}): ${response.statusText}`);
      }
      return await response.text();
    } catch (error) {
      console.error(`Error fetching ${type}:`, error);
      return null;
    }
  };

  /**
   * Fetch OLS / Launch Pad portal data.
   * `/milestone-1` → v1 `/quicklinks/canvas`; all other routes → v2 `/quicklinks/school-link`.
   */
  const fetchSchoolLink = async (): Promise<SchoolLinkSnapshot> => {
    if (!oktaAccessToken) {
      console.warn("No Okta token available. Skipping canvas API call.");
      return EMPTY_SCHOOL_LINK_SNAPSHOT;
    }

    const apiUrl = useCanvasQuicklinkApi ? CANVAS_QUICKLINK_API_URL : SCHOOL_LINK_API_URL;

    try {
      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${oktaAccessToken}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`API Error (${response.status}): ${response.statusText}`);
      }

      if (useCanvasQuicklinkApi) {
        const raw = await response.text();
        return parseCanvasQuicklinkBody(raw);
      }

      const data: SchoolLinkApiResponse = await response.json();
      return parseSchoolLinkResponse(data);
    } catch (error) {
      console.error("Error fetching canvas link:", error);
      return EMPTY_SCHOOL_LINK_SNAPSHOT;
    }
  };

  /**
   * Fetch acknowledgement API
   */
  const fetchAcknowledgement = async (
    schoolId: string,
    schoolShortName: string
  ): Promise<void> => {
    if (!oktaAccessToken) {
      console.warn("No Okta token available. Skipping acknowledgement API call.");
      return;
    }

    if (!schoolId && !schoolShortName) {
      console.error("Missing required query params: schoolId or schoolShortName");
      return;
    }

    try {
      const url = ACKNOWLEDGEMENT_API_URL(schoolId, schoolShortName);

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${oktaAccessToken}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`API Error (${response.status}): ${response.statusText}`);
      }

      const data: AcknowledgementLink = await response.json();
      setAcknowledgement(data);
    } catch (error) {
      console.error("Error fetching acknowledgement:", error);
      setAcknowledgement(null);
    }
  };

  useEffect(() => {
    if (!oktaAccessToken) {
      setQuicklinksLoaded(true);
      return;
    }

    const claimRole = roleFromOktaClaims(authState);
    const teacherPreview = isTeacherPreview(userRoleVal) || isTeacherPreview(claimRole);
    const roleStillUnknown = !userRoleVal && !claimRole && overviewLoading;
    if (roleStillUnknown) return;

    let cancelled = false;

    const fetchLinks = async () => {
      try {
        const [myAccount, k12zone, linkSnapshot] = await Promise.all([
          teacherPreview ? Promise.resolve(null) : fetchQuickLink("myaccount"),
          fetchQuickLink("k12zone"),
          fetchSchoolLink(),
        ]);
        if (cancelled) return;
        setMyAccountLink(myAccount);
        setK12ZoneLink(k12zone);
        setSchoolLink(linkSnapshot);
      } finally {
        if (!cancelled) setQuicklinksLoaded(true);
      }
    };

    setQuicklinksLoaded(false);
    void fetchLinks();

    return () => {
      cancelled = true;
    };
  }, [oktaAccessToken, authState, userRoleVal, overviewLoading, useCanvasQuicklinkApi]);

  return (
    <LinksContext.Provider value={{ myAccountLink, k12ZoneLink, schoolLink, quicklinksLoaded, acknowledgement, fetchAcknowledgement }}>
      {children}
    </LinksContext.Provider>
  );
};

export const useLinksContext = () => {
  const context = useContext(LinksContext);
  if (!context) {
    throw new Error("useLinksContext must be used within a LinksProvider");
  }
  return context;
};
